import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n";
import { toast } from "sonner";
import api, { mediaUrl } from "@/lib/api";
import Seo from "@/components/Seo";
import { orgSchema, breadcrumb, faqSchema } from "@/lib/seo";
import CityAutocomplete, { AIRPORT } from "@/components/CityAutocomplete";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { MagnifyingGlass, Airplane, ArrowRight, Path, Clock, UsersThree, Suitcase, Drop, WifiHigh, CalendarX, Baby, CircleNotch, MapPin } from "@phosphor-icons/react";

const COUNTRY_LABEL = { CH: "🇨🇭 Schweiz", DE: "🇩🇪 Deutschland", FR: "🇫🇷 Frankreich", IT: "🇮🇹 Italien", AT: "🇦🇹 Österreich", LI: "🇱🇮 Liechtenstein", FL: "🇱🇮 Liechtenstein" };
const VEH_FALLBACK = "https://customer-assets.emergentagent.com/job_zurich-airport-cab/artifacts/x5ooji4t_IMG-20260704-WA0000.jpg";

export default function Prices() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [fares, setFares] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const [pickup, setPickup] = useState(AIRPORT);
  const [dropoff, setDropoff] = useState(null);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const autoplay = useRef(Autoplay({ delay: 3000, stopOnInteraction: true, stopOnMouseEnter: true }));

  const GRAD = {
    economy: "from-[#DCEFE7] to-white",
    van: "from-[#FCE8D8] to-white",
    first: "from-[#DEE9FB] to-white",
    business: "from-[#E7E2F6] to-white",
    sprinter: "from-[#F1EAD9] to-white",
  };

  const amenities = [
    { icon: Drop, key: "amen_water" },
    { icon: WifiHigh, key: "amen_wifi" },
    { icon: CalendarX, key: "amen_cancel" },
    { icon: Baby, key: "amen_childseat" },
  ];

  useEffect(() => {
    api.get("/fares/all").then((r) => { setFares(r.data.fares || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? fares.filter((f) => f.name.toLowerCase().includes(q)) : fares;
  }, [fares, query]);

  const runCalc = async (p, d) => {
    if (!p || !d || p.lat == null || d.lat == null) return;
    setCalcLoading(true);
    try {
      const { data } = await api.post("/fare/vehicles", {
        pickup_id: p.id, dropoff_id: d.id,
        pickup_name: p.name, dropoff_name: d.name,
        pickup_lat: p.lat, pickup_lon: p.lon,
        dropoff_lat: d.lat, dropoff_lon: d.lon,
        service_type: "transfer",
      });
      setResult(data);
      setSelected(data.vehicles?.[0]?.vehicle_class || null);
    } catch (e) {
      setResult(null);
      toast.error(e?.response?.data?.detail || t("err_both_foreign"));
    }
    setCalcLoading(false);
  };

  // Auto-show all vehicle prices as soon as both addresses are selected (no button click needed).
  useEffect(() => {
    if (pickup?.lat != null && dropoff?.lat != null) runCalc(pickup, dropoff);
  }, [pickup, dropoff]); // eslint-disable-line

  const bookVehicle = (vClass) => {
    const today = new Date().toISOString().slice(0, 10);
    nav("/book", { state: { service_type: "transfer", pickup_id: pickup.id, pickup_name: pickup.name, pickup_lat: pickup.lat, pickup_lon: pickup.lon, dropoff_id: dropoff.id, dropoff_name: dropoff.name, dropoff_lat: dropoff.lat, dropoff_lon: dropoff.lon, date: today, time: "10:00", vehicle_class: vClass } });
  };

  const faqs = lang === "de" ? [
    { q: "Wie werden die Preise für den Airport Transfer Zurich berechnet?", a: "Jeder Transfer hat einen Festpreis, der auf der Distanz zwischen dem Flughafen Zürich und Ihrem Ziel basiert. Der angezeigte Preis gilt pro Fahrzeug – ohne Taxameter und ohne versteckte Kosten." },
    { q: "Sind die angezeigten Preise inklusive aller Gebühren?", a: "Ja, die Festpreise verstehen sich inklusive Fahrzeug, Chauffeur und Meet & Greet. Für Nachtfahrten (22–06 Uhr) kann ein Zuschlag anfallen; grössere Fahrzeuge (Van/Sprinter) werden separat berechnet." },
    { q: "Gilt der Preis pro Person oder pro Fahrzeug?", a: "Alle Preise gelten pro Fahrzeug, nicht pro Person – ideal für Familien und Gruppen." },
  ] : [
    { q: "How are Zürich Airport transfer prices calculated?", a: "Every transfer has a fixed fare based on the distance between Zürich Airport and your destination. The price shown is per vehicle – no taxi meter and no hidden costs." },
    { q: "Do the prices include all fees?", a: "Yes, the fixed fares include the vehicle, chauffeur and meet & greet. A night surcharge (22:00–06:00) may apply; larger vehicles (van/sprinter) are charged separately." },
    { q: "Is the price per person or per vehicle?", a: "All prices are per vehicle, not per person – ideal for families and groups." },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="prices-page">
      <Seo
        title={t("seo_prices_title")}
        description={t("seo_prices_desc")}
        path="/preise"
        jsonLd={[orgSchema(), breadcrumb([{ name: "Home", path: "/" }, { name: t("nav_prices"), path: "/preise" }]), faqSchema(faqs)]}
      />
      <p className="font-body text-sm text-[#EF5A1E] font-semibold mb-3">{t("nav_prices")}</p>
      <h1 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl text-[#0B1533] leading-[1.05] max-w-3xl">{t("prices_title")}</h1>
      <p className="font-body text-[#5A607A] mt-5 text-lg max-w-2xl">{t("prices_sub")}</p>

      {/* FARE CALCULATOR */}
      <div className="mt-10 rounded-3xl bg-[#0B1533] p-6 sm:p-8 elev-lg" data-testid="fare-calculator">
        <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl text-white">{t("calc_title")}</h2>
        <p className="font-body text-sm text-white/70 mt-2 max-w-xl">{t("calc_sub")}</p>
        <div className="mt-6 grid md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="font-body text-xs text-white/60 mb-1.5 block">{t("calc_pickup")}</label>
            <CityAutocomplete initial={pickup} accent onSelect={setPickup} placeholder={t("calc_addr_ph")} testid="calc-pickup" />
          </div>
          <div>
            <label className="font-body text-xs text-white/60 mb-1.5 block">{t("calc_dropoff")}</label>
            <CityAutocomplete initial={dropoff} onSelect={setDropoff} placeholder={t("calc_addr_ph")} testid="calc-dropoff" />
          </div>
        </div>
        {calcLoading && (
          <div className="mt-4 flex items-center gap-2 font-body text-sm text-white/70" data-testid="calc-loading">
            <CircleNotch size={16} className="animate-spin" /> {t("calc_calculating")}
          </div>
        )}

        {result && (
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 font-body text-sm text-white/80" data-testid="calc-route-summary">
            <span className="flex items-center gap-1.5"><MapPin size={15} className="text-[#EF5A1E]" /><span className="text-white font-medium">{result.pickup_name}</span> → <span className="text-white font-medium">{result.dropoff_name}</span></span>
            <span className="flex items-center gap-1.5"><Path size={14} /> {result.distance_km} km</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> ≈ {result.eta_minutes} min</span>
          </div>
        )}
      </div>

      {/* VEHICLE PRICE CAROUSEL */}
      {result ? (
        <div className="mt-10" data-testid="vehicle-carousel">
          <Carousel opts={{ loop: true, align: "start" }} plugins={[autoplay.current]} className="w-full">
            <div className="flex items-center justify-between gap-4 mb-6">
              <h2 className="font-heading font-bold tracking-tight text-2xl sm:text-3xl text-[#0B1533]">{t("calc_route")}</h2>
              <div className="hidden sm:flex gap-2 shrink-0">
                <CarouselPrevious className="static translate-y-0 h-10 w-10 border-[#E9E5DE] text-[#0B1533] hover:bg-[#FAFAF8]" data-testid="carousel-prev" />
                <CarouselNext className="static translate-y-0 h-10 w-10 border-[#E9E5DE] text-[#0B1533] hover:bg-[#FAFAF8]" data-testid="carousel-next" />
              </div>
            </div>
            <CarouselContent className="-ml-3 sm:-ml-4">
              {result.vehicles.map((v) => {
                const active = selected === v.vehicle_class;
                return (
                  <CarouselItem key={v.vehicle_class} className="pl-3 sm:pl-4 basis-[88%] sm:basis-1/2 lg:basis-1/3">
                    <div
                      onClick={() => setSelected(v.vehicle_class)}
                      data-testid={`veh-price-card-${v.vehicle_class}`}
                      className={`h-full rounded-3xl bg-white overflow-hidden flex flex-col cursor-pointer transition-all duration-200 border-2 ${active ? "border-[#EF5A1E] elev" : "border-transparent elev-soft hover:border-[#C9D2FF]"}`}
                    >
                      <div className={`h-40 bg-gradient-to-b ${GRAD[v.vehicle_class] || "from-[#EEF1FF] to-white"} overflow-hidden`}>
                        <img src={mediaUrl(v.image) || VEH_FALLBACK} alt={v.label} className="w-full h-full object-cover" />
                      </div>
                      <div className="p-4 sm:p-5 flex flex-col flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="font-heading font-semibold text-base sm:text-lg tracking-tight text-[#0B1533] leading-tight">{v.label}</h3>
                          <span className="font-heading font-bold text-base sm:text-lg text-[#0B1533] tabular-nums shrink-0" data-testid={`veh-price-${v.vehicle_class}`}>CHF {v.total.toFixed(0)}</span>
                        </div>
                        <div className="flex items-center gap-4 mt-2 font-body text-sm text-[#5A607A]">
                          <span className="flex items-center gap-1.5"><UsersThree size={17} weight="regular" /> {v.capacity}</span>
                          <span className="flex items-center gap-1.5"><Suitcase size={16} weight="regular" /> {v.luggage}</span>
                        </div>
                        {result.night && <p className="font-body text-[11px] text-[#5A607A] mt-1">{t("calc_night_note")}</p>}
                        <ul className="mt-4 space-y-2 flex-1">
                          {amenities.map((a, i) => (
                            <li key={i} className="flex items-center gap-2 font-body text-[13px] text-[#5A607A]">
                              <a.icon size={16} weight="regular" className="text-[#EF5A1E] shrink-0" />
                              {t(a.key)}
                            </li>
                          ))}
                        </ul>
                        <button
                          data-testid={`veh-book-${v.vehicle_class}`}
                          onClick={(e) => { e.stopPropagation(); bookVehicle(v.vehicle_class); }}
                          className={`group mt-5 w-full flex items-center justify-center gap-2 font-body font-semibold text-sm rounded-full py-3 transition-colors duration-200 ${active ? "bg-[#EF5A1E] text-white hover:bg-[#D2450F]" : "bg-[#0B1533] text-white hover:bg-[#EF5A1E]"}`}
                        >
                          {t("calc_book")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform duration-200" />
                        </button>
                      </div>
                    </div>
                  </CarouselItem>
                );
              })}
            </CarouselContent>
          </Carousel>
        </div>
      ) : (
        <div className="mt-8 rounded-3xl border border-dashed border-[#E9E5DE] bg-[#FAFAF8] px-6 py-12 text-center" data-testid="calc-empty">
          <Airplane size={30} weight="fill" className="text-[#EF5A1E] mx-auto mb-3" />
          <p className="font-body text-sm text-[#5A607A] max-w-md mx-auto">{t("calc_empty")}</p>
        </div>
      )}

      {/* ALL DESTINATION PRICES */}
      <h2 className="font-heading font-bold tracking-tight text-2xl text-[#0B1533] mt-16 mb-5">{t("calc_all_dest")}</h2>
      <div className="relative max-w-md">
        <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#9CA0B0]" />
        <input
          data-testid="prices-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("prices_search_ph")}
          className="w-full bg-white border border-[#E9E5DE] rounded-full pl-11 pr-4 py-3 font-body text-sm text-[#0B1533] focus:border-[#EF5A1E] focus:ring-2 focus:ring-[#EF5A1E]/15 focus:outline-none transition-all duration-200"
        />
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E9E5DE] bg-white">
        <div className="hidden sm:grid grid-cols-[1fr_auto_auto_auto] gap-4 px-6 py-3 bg-[#F5F2ED] font-body text-[11px] uppercase tracking-wider text-[#5A607A]">
          <span>{t("prices_col_dest")}</span>
          <span className="text-right w-24">{t("dest_distance")}</span>
          <span className="text-right w-24">{t("dest_travel_time")}</span>
          <span className="text-right w-28">{t("dest_price_from")}</span>
        </div>
        {loading ? (
          <div className="px-6 py-10 text-center font-body text-sm text-[#5A607A]" data-testid="prices-loading">…</div>
        ) : filtered.length === 0 ? (
          <div className="px-6 py-10 text-center font-body text-sm text-[#5A607A]">{t("prices_none")}</div>
        ) : (
          <div className="divide-y divide-[#F0EDE7]">
            {filtered.map((f) => (
              <Link key={f.id} to={`/destinations/${f.id}`} data-testid={`price-row-${f.id}`}
                className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_auto_auto_auto] gap-4 px-6 py-3.5 items-center hover:bg-[#FAFAF8] transition-colors duration-150 group">
                <span className="font-body text-sm text-[#0B1533] flex items-center gap-2 min-w-0">
                  <Airplane size={14} weight="fill" className="text-[#EF5A1E] shrink-0" />
                  <span className="truncate">{f.name}</span>
                  {f.country !== "CH" && <span className="font-body text-[10px] font-semibold text-[#EF5A1E] shrink-0">{f.country}</span>}
                </span>
                <span className="hidden sm:flex items-center justify-end gap-1 w-24 font-body text-sm text-[#5A607A]"><Path size={13} />{f.distance_km} km</span>
                <span className="hidden sm:flex items-center justify-end gap-1 w-24 font-body text-sm text-[#5A607A]"><Clock size={13} />{f.eta_minutes} min</span>
                <span className="text-right w-auto sm:w-28 font-heading font-bold text-[#0B1533] tabular-nums">CHF {f.total.toFixed(0)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
      <p className="font-body text-xs text-[#9CA0B0] mt-4">{t("prices_note")}</p>

      <div className="mt-12">
        <h2 className="font-heading font-bold text-2xl tracking-tight text-[#0B1533] mb-5">{t("faq_title")}</h2>
        <div className="space-y-4">
          {faqs.map((fq, i) => (
            <div key={i} className="rounded-2xl border border-[#E9E5DE] bg-white p-5" data-testid={`prices-faq-${i}`}>
              <p className="font-body font-semibold text-[#0B1533]">{fq.q}</p>
              <p className="font-body text-sm text-[#5A607A] mt-2 leading-relaxed">{fq.a}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
